import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Toast, type ToastAction, type ToastVariant } from "../molecules/Toast.js";
import { PERSISTENT_LAYER_ATTR, useT } from "@mmm/ui";
interface ToastRequest {
  variant: ToastVariant;
  title: string;
  message?: string;
  action?: ToastAction;
}

interface ToastItem extends ToastRequest {
  id: string;
}

type PushToastFn = (toast: ToastRequest) => void;

const ToastContext = createContext<PushToastFn | null>(null);

const AUTO_DISMISS_MS = 6000;
const MAX_TOASTS = 3;

/**
 * Which toasts survive a push, given who is attended to.
 *
 * Pulled out of `push` as a pure function so eviction can be tested directly
 * against a constructed `attended` set -- including "everyone is attended",
 * which never arises by accident from a sequence of real pushes and hovers,
 * only by asking for it directly.
 *
 * Evicts the oldest UNATTENDED toast first, not simply the oldest. Dropping
 * whatever came first took a toast away mid-read, and if its Sign in or
 * Dismiss button held focus, that went with it -- a burst of failing commands
 * is exactly when a toast is most likely to be both present and in use. If
 * everyone on screen is attended, the cap gives way: a toast beyond the limit
 * for a moment is a smaller harm than pulling one out from under its reader.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function evictForCap(
  toasts: readonly ToastItem[],
  attended: ReadonlySet<string>,
  cap: number = MAX_TOASTS
): ToastItem[] {
  if (toasts.length === 0) return [];
  const kept = [...toasts];
  const justArrived = kept[kept.length - 1]!.id;

  let over = kept.length - cap;
  for (let i = 0; i < kept.length && over > 0; ) {
    const candidate = kept[i]!;
    if (candidate.id !== justArrived && !attended.has(candidate.id)) {
      kept.splice(i, 1);
      over -= 1;
    } else {
      i += 1;
    }
  }
  return kept;
}

let nextId = 0;
// Module-level pointer to the mounted provider's push function. Lets plain-TS
// callers (api/commands.ts is not a React module) push toasts without a
// hook — mirrors how api/saveStatus.ts exposes trackSave() as a plain
// function backed by a React subscription underneath.
let externalPush: PushToastFn | null = null;

/**
 * Escape hatch for non-component callers. No-ops (drops the toast) if no
 * ToastProvider is currently mounted — acceptable for a transient
 * notification; there is deliberately no queue/replay.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function pushToast(toast: ToastRequest): void {
  externalPush?.(toast);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const t = useT();

  // Which toasts someone is currently on, by pointer or keyboard. A ref, not
  // state: this only decides which toast may be evicted, and re-rendering the
  // stack every time a pointer crosses one would be pure churn.
  const attendedRef = useRef(new Set<string>());

  const setAttended = useCallback((id: string, attended: boolean) => {
    if (attended) attendedRef.current.add(id);
    else attendedRef.current.delete(id);
  }, []);

  const dismiss = useCallback((id: string) => {
    attendedRef.current.delete(id);
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback<PushToastFn>((toast) => {
    const id = `toast-${++nextId}`;
    // Cap the stack so a burst of failures (e.g. several optimistic map moves
    // in a row while the API is down) doesn't fill the screen. See
    // evictForCap for which toast that costs.
    setToasts((prev) => evictForCap([...prev, { id, ...toast }], attendedRef.current));
  }, []);

  useEffect(() => {
    externalPush = push;
    return () => {
      if (externalPush === push) externalPush = null;
    };
  }, [push]);

  return (
    <ToastContext.Provider value={push}>
      {children}
      {/* Portalled to <body> with its own layer marker, rather than living
          inside the app root. A modal makes the root inert, and a toast is
          exactly the thing that must survive that: a save can fail while its
          dialog stays deliberately open, and the notice about it -- visible,
          with buttons -- was unclickable and missing from the accessibility
          tree, which is worse than not showing it at all. */}
      {createPortal(
        <div
          className="mmm-toast-stack"
          aria-label={t("common.notifications")}
          {...{ [PERSISTENT_LAYER_ATTR]: "" }}
        >
          {/* `dismiss` is passed whole, rather than a per-toast arrow built
              here: an inline closure is a new function on every provider
              render, which changed each timer's identity and restarted it --
              see ToastTimer. */}
          {toasts.map((toast) => (
            <ToastTimer key={toast.id} toast={toast} dismiss={dismiss} onAttended={setAttended} />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

/**
 * Owns the auto-dismiss timer for one toast, pausing it while the reader is on
 * it — by pointer or by keyboard.
 *
 * Hover alone was not enough once a toast became reachable by Tab from inside a
 * modal: a session-expired notice could take its own "Sign in" button away
 * mid-keystroke, six seconds after appearing, while it held focus.
 */
export function ToastTimer({
  toast,
  dismiss,
  onAttended,
}: {
  toast: ToastItem;
  dismiss: (id: string) => void;
  onAttended: (id: string, attended: boolean) => void;
}) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Built here from two stable values, so it lasts as long as this toast does.
  // Taking a ready-made `() => dismiss(toast.id)` from the provider meant a new
  // function on every provider render, which changed `startTimer` and re-ran
  // the effect below -- restarting the countdown from scratch, with none of the
  // pause flags consulted. Any other toast arriving or leaving was enough to
  // put six fresh seconds on a toast that was being read at the time.
  const onDismiss = useCallback(() => dismiss(toast.id), [dismiss, toast.id]);
  // Two independent reasons to hold the countdown, tracked separately because
  // they overlap: a pointer resting on the toast while the keyboard is also in
  // it is ordinary, and either one ending used to restart dismissal on its own.
  // Moving the mouse away then dropped a toast the keyboard was still inside,
  // and tabbing out dropped one the pointer was still over.
  const hoveredRef = useRef(false);
  const focusedRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const startTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(onDismiss, AUTO_DISMISS_MS);
  }, [onDismiss]);

  /** Resumes only once nothing is holding it any more. */
  const resumeIfUnattended = useCallback(() => {
    if (hoveredRef.current || focusedRef.current) return;
    startTimer();
  }, [startTimer]);

  /** Tells the provider whether anyone is on this toast, for eviction. */
  const reportAttendance = useCallback(() => {
    onAttended(toast.id, hoveredRef.current || focusedRef.current);
  }, [onAttended, toast.id]);

  useEffect(() => {
    startTimer();
    return clearTimer;
  }, [startTimer]);

  // Stop claiming attention once this toast is gone, so a stale id cannot
  // protect a slot that no longer exists.
  useEffect(() => {
    const { id } = toast;
    return () => onAttended(id, false);
  }, [onAttended, toast]);

  return (
    <Toast
      variant={toast.variant}
      title={toast.title}
      message={toast.message}
      action={toast.action}
      onDismiss={onDismiss}
      onMouseEnter={() => {
        hoveredRef.current = true;
        reportAttendance();
        clearTimer();
      }}
      onMouseLeave={() => {
        hoveredRef.current = false;
        reportAttendance();
        resumeIfUnattended();
      }}
      onFocusEnter={() => {
        focusedRef.current = true;
        reportAttendance();
        clearTimer();
      }}
      onFocusLeave={() => {
        focusedRef.current = false;
        reportAttendance();
        resumeIfUnattended();
      }}
    />
  );
}

/** Returns a pushToast(opts) function for use inside React components. */
// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): PushToastFn {
  const push = useContext(ToastContext);
  if (!push) throw new Error("useToast must be used inside ToastProvider");
  return push;
}
